import { useState, useEffect, useRef, useId, type ComponentType } from 'react';
import { useTheme } from '@vitness/theme';
import { vp } from '@vitness/tokens';
import { MoreHorizontal, Settings2, type LucideProps } from '@vitness/icons';

/**
 * A nav label inside a fixed-width cell. `minWidth: 0` because a grid/flex item
 * refuses to shrink below its content otherwise, which is what let "Projections"
 * push its cell wider than the fifth of the row it was allotted.
 */
const LABEL_STYLE = {
  fontFamily: 'var(--vn-font-body)',
  fontSize: 10,
  lineHeight: 1.1,
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

export interface MobileNavItem {
  id: string;
  label: string;
  icon: ComponentType<LucideProps>;
  href?: string;
  onClick?: () => void;
}

export interface MobileNavProps {
  items: MobileNavItem[];
  activeId?: string;
  onNavigate?: (id: string) => void;
  maxItems?: number;
  /** When provided, only these item IDs appear in the bar (in original items order). */
  pinnedIds?: string[];
  /** Called when the user toggles an item in/out of the pinned set. */
  onPinnedChange?: (ids: string[]) => void;
  /** Enables the Customize panel in the More sheet. */
  editable?: boolean;
  /** Prevents opening the Customize panel even when editable. */
  locked?: boolean;
  /** When true, uses absolute positioning for demo embedding inside a container. */
  contained?: boolean;
}

export function MobileNav({
  items,
  activeId,
  onNavigate,
  maxItems = 5,
  pinnedIds,
  onPinnedChange,
  editable = false,
  locked = false,
  contained = false,
}: MobileNavProps) {
  const { accentRgb, accentSafe } = useTheme();
  const rs = `${accentRgb.r},${accentRgb.g},${accentRgb.b}`;
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const sheetTitleId = useId();

  const closeOverflow = () => {
    setOverflowOpen(false);
    setCustomizing(false);
  };

  /*
   * The overflow panel is a modal surface, so it owes the three things every
   * modal owes: a name, an Escape key, and somewhere sane for focus to go.
   * Previously it had none — the only way out was tapping the scrim, which
   * leaves keyboard and switch-control users stuck behind it.
   *
   * Focus moves into the panel on open and back to the More button on close,
   * so dismissing it doesn't dump focus at the top of the document.
   */
  useEffect(() => {
    if (!overflowOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOverflowOpen(false);
        setCustomizing(false);
        moreRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    sheetRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [overflowOpen]);

  const pinnedSet = pinnedIds ? new Set(pinnedIds) : null;

  const visibleItems = pinnedSet
    ? items.filter(item => pinnedSet.has(item.id))
    : items.length > maxItems
      ? items.slice(0, maxItems - 1)
      : items;

  const overflowItems = pinnedSet
    ? items.filter(item => !pinnedSet.has(item.id))
    : items.length > maxItems
      ? items.slice(maxItems - 1)
      : [];

  const hasOverflow = overflowItems.length > 0;
  const showMoreButton = hasOverflow || (editable && !locked);
  const overflowActive = overflowItems.some(item => item.id === activeId);

  const canCustomize = editable && !locked;

  useEffect(() => {
    if (locked) setCustomizing(false);
  }, [locked]);
  const pinnedCount = visibleItems.length;

  function togglePin(id: string) {
    if (!pinnedIds || !onPinnedChange) return;
    const isPinned = pinnedIds.includes(id);
    if (isPinned && pinnedCount <= 1) return;
    if (!isPinned && pinnedCount >= maxItems) return;
    const next = isPinned
      ? pinnedIds.filter(pid => pid !== id)
      : [...pinnedIds, id];
    onPinnedChange(next);
  }

  const pos = contained ? 'absolute' as const : 'fixed' as const;

  return (
    <>
      {overflowOpen && (
        <>
          <div
            aria-hidden="true"
            onClick={closeOverflow}
            style={{
              position: pos,
              inset: 0,
              zIndex: 39,
              background: 'rgba(0,0,0,0.3)',
              transition: 'opacity 0.15s ease',
            }}
          />
          <div
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={sheetTitleId}
            tabIndex={-1}
            style={{
              position: pos,
              bottom: 'calc(56px + var(--vn-safe-bottom, 0px))',
              left: 0,
              right: 0,
              zIndex: 41,
              background: 'var(--vn-surface-card)',
              borderTop: '1px solid var(--vn-border-default)',
              borderRadius: '12px 12px 0 0',
              boxShadow: '0 -4px 20px rgba(0,0,0,0.16)',
              padding: '16px 12px',
              // Corrected for the interface scale — see tokens/zoom.ts.
              maxHeight: vp('50vh'),
              overflowY: 'auto',
              outline: 'none',
            }}
          >
            {/* The panel's accessible name. Visible when there are overflow
                items to head; still present (visually hidden) when the panel is
                only the Customize control, because `aria-labelledby` must
                resolve either way. */}
            <h2
              id={sheetTitleId}
              style={overflowItems.length > 0 ? {
                fontFamily: 'var(--vn-font-mono)',
                fontSize: 9,
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--vn-text-disabled)',
                margin: '0 0 12px',
                paddingLeft: 4,
              } : {
                position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
                overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
              }}
            >
              {overflowItems.length > 0 ? 'More' : 'Navigation shortcuts'}
            </h2>
            {overflowItems.length > 0 && (
              <>
                {/* auto-fit, not `repeat(5, 1fr)`: five fixed columns give each
                    label ~64px on a 360px phone, which is narrower than
                    "Projections" and most of the other destinations. Sized off
                    a floor instead, the grid drops to four or three columns on
                    a narrow phone and goes past five on a wide one. */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(72px, 1fr))',
                  gap: 4,
                }}>
                  {overflowItems.map(item => {
                    const isActive = activeId === item.id;
                    const IconComp = item.icon;
                    return (
                      <button
                        key={item.id}
                        aria-current={isActive ? 'page' : undefined}
                        onClick={() => {
                          onNavigate?.(item.id);
                          item.onClick?.();
                          closeOverflow();
                        }}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 4,
                          padding: '10px 4px',
                          borderRadius: 8,
                          border: 'none',
                          background: isActive ? `rgba(${rs},0.08)` : 'transparent',
                          cursor: 'pointer',
                          color: isActive ? accentSafe : 'var(--vn-text-muted)',
                          transition: 'background 0.12s ease',
                        }}
                      >
                        <IconComp
                          size={22}
                          color={isActive ? accentSafe : 'var(--vn-text-muted)'}
                          strokeWidth={isActive ? 2 : 1.7}
                        />
                        <span style={{ ...LABEL_STYLE, fontWeight: isActive ? 600 : 400 }}>
                          {item.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {canCustomize && (
              <>
                {overflowItems.length > 0 && (
                  <div style={{
                    height: 1,
                    background: 'var(--vn-border-default)',
                    margin: '12px 0',
                  }} />
                )}
                <button
                  onClick={() => setCustomizing(prev => !prev)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    width: '100%',
                    padding: '8px 4px',
                    border: 'none',
                    borderRadius: 6,
                    background: customizing ? `rgba(${rs},0.08)` : 'transparent',
                    cursor: 'pointer',
                    fontFamily: 'var(--vn-font-mono)',
                    fontSize: 10,
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: customizing ? accentSafe : 'var(--vn-text-secondary)',
                    transition: 'all 0.12s ease',
                  }}
                >
                  <Settings2 size={14} color={customizing ? accentSafe : 'var(--vn-text-muted)'} strokeWidth={1.7} />
                  Customize shortcuts
                </button>

                {customizing && (
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                    marginTop: 10,
                  }}>
                    {items.map(item => {
                      const isPinned = pinnedSet?.has(item.id) ?? false;
                      const atMin = isPinned && pinnedCount <= 1;
                      const atMax = !isPinned && pinnedCount >= maxItems;
                      const disabled = atMin || atMax;
                      const IconComp = item.icon;
                      return (
                        <button
                          key={item.id}
                          onClick={() => !disabled && togglePin(item.id)}
                          disabled={disabled}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 5,
                            padding: '5px 10px',
                            borderRadius: 6,
                            border: isPinned
                              ? `1px solid rgba(${rs},0.4)`
                              : '1px solid var(--vn-border-default)',
                            background: isPinned ? `rgba(${rs},0.08)` : 'transparent',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled ? 0.4 : 1,
                            fontFamily: 'var(--vn-font-body)',
                            fontSize: 11,
                            fontWeight: isPinned ? 600 : 400,
                            color: isPinned ? accentSafe : 'var(--vn-text-secondary)',
                            transition: 'all 0.12s ease',
                          }}
                        >
                          <IconComp
                            size={14}
                            color={isPinned ? accentSafe : 'var(--vn-text-muted)'}
                            strokeWidth={isPinned ? 2 : 1.5}
                          />
                          {item.label}
                        </button>
                      );
                    })}
                    <div style={{
                      width: '100%',
                      fontFamily: 'var(--vn-font-mono)',
                      fontSize: 9,
                      color: 'var(--vn-text-disabled)',
                      marginTop: 4,
                    }}>
                      {pinnedCount}/{maxItems} shortcuts
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      <nav
        // Named, because the shell already has a "Primary navigation" landmark
        // (the drawer) and two unlabelled <nav>s in the same document are
        // indistinguishable in a screen reader's landmark list.
        aria-label="Quick navigation"
        style={{
          position: pos,
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          // calc, not a fixed 56: with border-box the safe-area padding would
          // otherwise eat into the bar and squash the icons on notched phones.
          // `--vn-safe-bottom` is the clamped inset — iOS 26 over-reports the
          // raw one, which showed up as a band of dead tab-bar under the icons.
          height: 'calc(56px + var(--vn-safe-bottom, 0px))',
          paddingBottom: 'var(--vn-safe-bottom, 0px)',
          background: 'var(--vn-surface-raised)',
          borderTop: '1px solid var(--vn-border-default)',
          transition: 'background 0s, border-color 0s',
        }}
      >
        {visibleItems.map(item => {
          const isActive = activeId === item.id;
          const IconComp = item.icon;

          return (
            <button
              key={item.id}
              // The bar is the app's primary navigation, so the current
              // destination has to be announced, not just tinted — colour alone
              // is not a state a screen reader can read.
              aria-current={isActive ? 'page' : undefined}
              onClick={() => {
                onNavigate?.(item.id);
                item.onClick?.();
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                flex: 1,
                minWidth: 0,
                height: '100%',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: '4px 2px',
                transition: 'color 0.12s ease',
                color: isActive ? accentSafe : 'var(--vn-text-muted)',
              }}
            >
              <IconComp
                size={20}
                color={isActive ? accentSafe : 'var(--vn-text-muted)'}
                strokeWidth={isActive ? 2 : 1.7}
              />
              <span style={{ ...LABEL_STYLE, fontWeight: isActive ? 600 : 400 }}>
                {item.label}
              </span>
            </button>
          );
        })}

        {showMoreButton && (
          <button
            ref={moreRef}
            aria-expanded={overflowOpen}
            aria-haspopup="dialog"
            onClick={() => (overflowOpen ? closeOverflow() : setOverflowOpen(true))}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              flex: 1,
              minWidth: 0,
              height: '100%',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: '4px 2px',
              transition: 'color 0.12s ease',
              color: overflowActive || overflowOpen ? accentSafe : 'var(--vn-text-muted)',
            }}
          >
            <MoreHorizontal
              size={20}
              color={overflowActive || overflowOpen ? accentSafe : 'var(--vn-text-muted)'}
              strokeWidth={1.7}
            />
            <span style={{ ...LABEL_STYLE, fontWeight: overflowActive || overflowOpen ? 600 : 400 }}>
              More
            </span>
          </button>
        )}
      </nav>
    </>
  );
}
